import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NumCell } from "./NumCell";
import { WAGE_FREQS, staffHourly, staffWeeklyCost, convertStaffAmount, clamp, money } from "./rateMath";
import { FREQ_LABEL } from "./SimpleInputs";

const StaffRowView = ({ row, idx, onChange, onDelete }) => (
  <tr className="border-t border-gray-100" data-testid={`staff-row-${idx}`}>
    <td className="py-2 pr-2">
      <Input
        value={row.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder={`Staff ${idx + 1}`}
        className="h-9 text-sm border-gray-200 min-w-[140px]"
        data-testid={`staff-name-${idx}`}
      />
    </td>
    <td className="py-2 px-1">
      <NumCell value={row.wage_amount} onChange={(v) => onChange({ wage_amount: Math.max(v, 0) })} prefix="$" min={0} className="min-w-[100px]" testId={`staff-wage-${idx}`} />
    </td>
    <td className="py-2 px-1">
      <Select value={row.wage_frequency} onValueChange={(f) => onChange({ wage_frequency: f, wage_amount: convertStaffAmount(row, f) })}>
        <SelectTrigger className="h-9 w-[110px] text-xs" data-testid={`staff-frequency-${idx}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {WAGE_FREQS.map((f) => (
            <SelectItem key={f} value={f} data-testid={`staff-frequency-${idx}-${f}`}>{FREQ_LABEL[f]}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </td>
    <td className="py-2 px-1">
      <NumCell value={row.weekly_hours} onChange={(v) => onChange({ weekly_hours: clamp(v, 0, 168) })} min={0} max={168} className="min-w-[80px]" testId={`staff-hours-${idx}`} />
    </td>
    <td className="py-2 px-2 font-mono text-right text-gray-600 whitespace-nowrap" data-testid={`staff-hourly-${idx}`}>{money(staffHourly(row))}</td>
    <td className="py-2 px-2 font-mono text-right text-gray-900 whitespace-nowrap" data-testid={`staff-weekly-cost-${idx}`}>{money(staffWeeklyCost(row))}</td>
    <td className="py-2 pl-1">
      <Button variant="ghost" size="icon" onClick={onDelete} className="h-8 w-8 text-gray-400 hover:text-red-600" data-testid={`staff-delete-${idx}`}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </td>
  </tr>
);

export const StaffRoster = ({ rows, onRowsChange, weeklyTotal }) => {
  const update = (i, patch) => onRowsChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () =>
    onRowsChange([...rows, { id: `st-${Date.now()}`, name: "", wage_amount: 0, wage_frequency: "hourly", weekly_hours: 0 }]);
  return (
    <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50" data-testid="staff-roster">
      <div className="text-sm font-medium text-gray-800">Staff</div>
      <p className="text-xs text-gray-500 mt-1 mb-3 leading-relaxed">
        Everyone whose time goes into producing jobs — including yourself, if you're on the tools. Enter each person's wage in whatever unit you know it, plus the hours they work each week.
      </p>
      <div className="overflow-x-auto max-h-[440px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="text-left text-gray-500">
              <th className="pb-1 font-medium">Name (optional)</th>
              <th className="pb-1 px-1 font-medium">Wage</th>
              <th className="pb-1 px-1 font-medium">Per</th>
              <th className="pb-1 px-1 font-medium whitespace-nowrap">Hrs / week</th>
              <th className="pb-1 px-2 font-medium text-right">Hourly</th>
              <th className="pb-1 px-2 font-medium text-right whitespace-nowrap">Weekly cost</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <StaffRowView key={row.id} row={row} idx={i} onChange={(p) => update(i, p)} onDelete={() => onRowsChange(rows.filter((_, j) => j !== i))} />
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-xs text-gray-400 py-4 text-center">No staff added yet.</div>}
      </div>
      <div className="flex items-center justify-between mt-3">
        <Button variant="outline" size="sm" onClick={add} className="h-8 text-xs" data-testid="staff-add-btn">
          <Plus className="w-3 h-3 mr-1.5" /> Add Staff
        </Button>
        <div className="text-xs text-gray-600" data-testid="staff-total">
          {rows.length} {rows.length === 1 ? "person" : "people"} · Total labour:{" "}
          <span className="font-mono font-semibold text-gray-900">{money(weeklyTotal)}</span>/week
        </div>
      </div>
    </div>
  );
};
